import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { customersAPI, wpOffersAPI, WP_PRODUCT_GROUPS } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, Flame, Thermometer, Package, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";

const PRODUCT_GROUP_COLORS = {
  waermepumpe: "border-orange-300 bg-orange-50",
  speicher: "border-blue-300 bg-blue-50",
  hydraulik: "border-teal-300 bg-teal-50",
  installation: "border-purple-300 bg-purple-50",
  zusatz: "border-amber-300 bg-amber-50",
  sonstiges: "border-slate-300 bg-slate-50",
};

export default function WPOfferCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  // groupedItems: { [groupKey]: [ {description, quantity, unit, unit_price} ] }
  const initialGrouped = WP_PRODUCT_GROUPS.reduce((acc, g) => {
    acc[g.key] = [];
    return acc;
  }, {});

  const [formData, setFormData] = useState({
    customer_id: "",
    title: "Wärmepumpen-Angebot",
    payment_days: 14,
    include_vat: true,
    vat_rate: 19,
    notes: "",
    heat_demand_kwh: "",
    current_heating_type: "Gas",
    current_energy_cost_eur: "",
    expected_savings_eur: "",
    groupedItems: initialGrouped,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersRes, templatesRes] = await Promise.all([
        customersAPI.list(),
        wpOffersAPI.listTemplates().catch(() => ({ data: [] })),
      ]);
      setCustomers(customersRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (err) {
      toast.error("Fehler beim Laden der Kunden");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Lädt ein WP-Template in das Formular. Bestehende Positionen werden NICHT
   * gelöscht — die Template-Items werden in die jeweilige Produktgruppe
   * APPENDED. So lässt sich z. B. ein „Vitocal 250-A Standard-Paket" + manuell
   * eingetragene Zusatzpositionen kombinieren.
   * 
   * Wird auch als Fallback genutzt, wenn ein Reonic-Import nur eine
   * Pauschalsumme geliefert hat: User wählt das passende Template und hat
   * sofort konkrete Einzelpositionen statt 0 €-Platzhaltern.
   */
  const applyTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) return;
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    setFormData((prev) => {
      const grouped = { ...prev.groupedItems };
      tpl.items.forEach((it) => {
        const pg = it.product_group || "sonstiges";
        grouped[pg] = [
          ...(grouped[pg] || []),
          {
            description: it.description,
            quantity: it.quantity,
            unit: it.unit,
            unit_price: it.unit_price,
          },
        ];
      });
      return { ...prev, groupedItems: grouped };
    });
    const total = tpl.items.reduce((s, it) => s + (it.quantity * it.unit_price), 0);
    toast.success(
      `Template "${tpl.name}" geladen — ${tpl.items.length} Positionen, ${total.toFixed(2)} € netto.`
    );
  };

  const addItem = (groupKey) => {
    setFormData((prev) => ({
      ...prev,
      groupedItems: {
        ...prev.groupedItems,
        [groupKey]: [
          ...prev.groupedItems[groupKey],
          { description: "", quantity: 1, unit: "Stück", unit_price: 0 },
        ],
      },
    }));
  };

  const removeItem = (groupKey, idx) => {
    setFormData((prev) => ({
      ...prev,
      groupedItems: {
        ...prev.groupedItems,
        [groupKey]: prev.groupedItems[groupKey].filter((_, i) => i !== idx),
      },
    }));
  };

  const updateItem = (groupKey, idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      groupedItems: {
        ...prev.groupedItems,
        [groupKey]: prev.groupedItems[groupKey].map((it, i) =>
          i === idx ? { ...it, [field]: value } : it
        ),
      },
    }));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    Object.values(formData.groupedItems).forEach((items) => {
      items.forEach((it) => {
        subtotal += (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
      });
    });
    const vat = formData.include_vat ? subtotal * ((parseFloat(formData.vat_rate) || 0) / 100) : 0;
    return {
      subtotal: subtotal,
      vat: vat,
      total: subtotal + vat,
    };
  }, [formData.groupedItems, formData.include_vat, formData.vat_rate]);

  const handleSubmit = async () => {
    if (!formData.customer_id) {
      toast.error("Bitte einen Kunden auswählen");
      return;
    }

    const flatItems = [];
    Object.entries(formData.groupedItems).forEach(([groupKey, items]) => {
      items.forEach((it) => {
        if (it.description && it.description.trim()) {
          flatItems.push({
            description: it.description.trim(),
            quantity: parseFloat(it.quantity) || 1,
            unit: it.unit || "Stück",
            unit_price: parseFloat(it.unit_price) || 0,
            product_group: groupKey,
          });
        }
      });
    });

    if (flatItems.length === 0) {
      toast.error("Bitte mindestens eine Position hinzufügen");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customer_id: formData.customer_id,
        title: formData.title,
        items: flatItems,
        payment_days: parseInt(formData.payment_days) || 14,
        include_vat: formData.include_vat,
        vat_rate: parseFloat(formData.vat_rate) || 19,
        notes: formData.notes || null,
        heat_demand_kwh: formData.heat_demand_kwh ? parseFloat(formData.heat_demand_kwh) : null,
        current_heating_type: formData.current_heating_type || null,
        current_energy_cost_eur: formData.current_energy_cost_eur ? parseFloat(formData.current_energy_cost_eur) : null,
        expected_savings_eur: formData.expected_savings_eur ? parseFloat(formData.expected_savings_eur) : null,
      };
      const res = await wpOffersAPI.create(payload);
      toast.success(`WP-Angebot ${res.data.offer_number} erstellt`);
      navigate(`/wp-angebote/${res.data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Fehler beim Erstellen";
      toast.error(typeof msg === "string" ? msg : "Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="wp-offer-create-loading" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wp-offer-create-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
        </Button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Flame className="h-7 w-7 text-orange-500" />
            Neues Wärmepumpen-Angebot
          </h1>
          <p className="text-sm text-muted-foreground">Strukturiert in 5 Produktgruppen für ein verkaufsstarkes PDF</p>
        </div>
      </div>

      {/* Kunde & Stammdaten */}
      <Card>
        <CardHeader>
          <CardTitle>Kunde & Stammdaten</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Kunde *</Label>
            <Select
              value={formData.customer_id}
              onValueChange={(v) => setFormData({ ...formData, customer_id: v })}
            >
              <SelectTrigger data-testid="customer-select">
                <SelectValue placeholder="Kunde auswählen" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.customer_number ? `${c.customer_number} – ` : ""}
                    {c.first_name} {c.last_name} {c.company_name ? `(${c.company_name})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Titel</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              data-testid="title-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* WP-Spezifika */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Thermometer className="h-5 w-5 text-orange-500" />
            Wärme-Kennzahlen (optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-4">
          <div>
            <Label>Jahresheizwärmebedarf (kWh)</Label>
            <Input
              type="number"
              value={formData.heat_demand_kwh}
              onChange={(e) => setFormData({ ...formData, heat_demand_kwh: e.target.value })}
              placeholder="z.B. 12000"
              data-testid="heat-demand-input"
            />
          </div>
          <div>
            <Label>Aktuelles Heizsystem</Label>
            <Select
              value={formData.current_heating_type || ""}
              onValueChange={(v) => setFormData({ ...formData, current_heating_type: v })}
            >
              <SelectTrigger data-testid="heating-type-select">
                <SelectValue placeholder="Auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Gas">Gas</SelectItem>
                <SelectItem value="Öl">Öl</SelectItem>
                <SelectItem value="Pellets">Pellets</SelectItem>
                <SelectItem value="Strom (Direktheizung)">Strom (Direktheizung)</SelectItem>
                <SelectItem value="Fernwärme">Fernwärme</SelectItem>
                <SelectItem value="Sonstiges">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Aktuelle Heizkosten/Jahr (€)</Label>
            <Input
              type="number"
              value={formData.current_energy_cost_eur}
              onChange={(e) => setFormData({ ...formData, current_energy_cost_eur: e.target.value })}
              placeholder="z.B. 2800"
              data-testid="current-cost-input"
            />
          </div>
          <div>
            <Label>Erwartete Einsparung/Jahr (€)</Label>
            <Input
              type="number"
              value={formData.expected_savings_eur}
              onChange={(e) => setFormData({ ...formData, expected_savings_eur: e.target.value })}
              placeholder="z.B. 1200"
              data-testid="expected-savings-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* WP-Template Quickfill */}
      {templates.length > 0 && (
        <Card className="border-2 border-dashed border-blue-300 bg-blue-50/30" data-testid="wp-template-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Komponentenpaket laden
              <span className="text-xs font-normal text-muted-foreground ml-2">optional, fügt Positionen an</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs">Vorlage auswählen</Label>
                <Select value={selectedTemplate} onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="z. B. Vitocal 250-A Standard-Paket" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => {
                      const total = t.items.reduce((s, it) => s + (it.quantity * it.unit_price), 0);
                      return (
                        <SelectItem key={t.id} value={t.id} data-testid={`template-opt-${t.id}`}>
                          <span className="font-medium">{t.name}</span>
                          <span className="text-muted-foreground ml-2">— {t.items.length} Pos · {total.toLocaleString("de-DE", { maximumFractionDigits: 2 })} € netto</span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {selectedTemplate && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                  Erneut wählen lädt das Paket nochmals — Positionen werden additiv ergänzt.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Produktgruppen */}
      {WP_PRODUCT_GROUPS.map((group) => (
        <Card key={group.key} className={`border-2 ${PRODUCT_GROUP_COLORS[group.key] || ""}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{group.label}</CardTitle>
                {group.description && (
                  <p className="text-xs text-muted-foreground mt-1">{group.description}</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => addItem(group.key)}
                data-testid={`add-item-${group.key}`}
              >
                <Plus className="h-4 w-4 mr-1" /> Position
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {formData.groupedItems[group.key].length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                Noch keine Positionen in dieser Gruppe.
              </p>
            )}
            {formData.groupedItems[group.key].map((item, idx) => {
              const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
              return (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-end p-3 bg-white rounded-lg border"
                  data-testid={`wp-item-${group.key}-${idx}`}
                >
                  <div className="col-span-12 md:col-span-5">
                    <Label className="text-xs">Beschreibung</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(group.key, idx, "description", e.target.value)}
                      placeholder="z.B. Vaillant aroTHERM plus VWL 75/6"
                    />
                  </div>
                  <div className="col-span-3 md:col-span-1">
                    <Label className="text-xs">Menge</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateItem(group.key, idx, "quantity", e.target.value)}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <Label className="text-xs">Einheit</Label>
                    <Input
                      value={item.unit}
                      onChange={(e) => updateItem(group.key, idx, "unit", e.target.value)}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <Label className="text-xs">Einzelpreis (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateItem(group.key, idx, "unit_price", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1 text-right">
                    <Label className="text-xs">Summe</Label>
                    <div className="font-semibold py-2">{lineTotal.toFixed(2)} €</div>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeItem(group.key, idx)}
                      data-testid={`remove-item-${group.key}-${idx}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* Konditionen */}
      <Card>
        <CardHeader>
          <CardTitle>Konditionen</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Zahlungsziel (Tage)</Label>
            <Input
              type="number"
              value={formData.payment_days}
              onChange={(e) => setFormData({ ...formData, payment_days: e.target.value })}
              data-testid="payment-days-input"
            />
          </div>
          <div className="flex items-center gap-3 mt-6">
            <Switch
              checked={formData.include_vat}
              onCheckedChange={(c) => setFormData({ ...formData, include_vat: c })}
              data-testid="include-vat-switch"
            />
            <Label>MwSt. ausweisen</Label>
          </div>
          <div>
            <Label>MwSt.-Satz (%)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.vat_rate}
              onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
              disabled={!formData.include_vat}
              data-testid="vat-rate-input"
            />
          </div>
          <div className="md:col-span-3">
            <Label>Notizen</Label>
            <Textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optionale interne Notizen"
              data-testid="notes-textarea"
            />
          </div>
        </CardContent>
      </Card>

      {/* Summe */}
      <Card className="border-2 border-orange-300 bg-orange-50">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-end">
            <div className="text-right space-y-1">
              <div className="text-sm text-muted-foreground">
                Netto: <span className="font-semibold">{totals.subtotal.toFixed(2)} €</span>
              </div>
              {formData.include_vat && (
                <div className="text-sm text-muted-foreground">
                  MwSt. ({formData.vat_rate}%): <span className="font-semibold">{totals.vat.toFixed(2)} €</span>
                </div>
              )}
              <div className="text-2xl font-bold" data-testid="total-amount">
                Gesamt: {totals.total.toFixed(2)} €
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn">
          Abbrechen
        </Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="save-btn">
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          WP-Angebot erstellen
        </Button>
      </div>
    </div>
  );
}
